'use client'

import * as React from 'react'

import {
  Combobox,
  ComboboxButton,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxList,
  ComboboxSelectableItem,
  ComboboxTrigger,
} from '@/components/common/ComboboxV2'

import { PURSUIT_TYPES } from '@/constants/pursuit'

import { getOpportunityLabelFromPursuitType } from '@/helpers/pursuit'

import type { Alignment, Side } from '@/types/components/Radix'
import type { PursuitType } from '@/types/pursuit/Pursuit'

interface TypeSelectProps {
  value: PursuitType
  align?: Alignment
  side?: Side
  triggerClassName?: string
  onChange: (value: PursuitType) => void
}

export const TypeSelect: React.FC<TypeSelectProps> = ({
  value,
  align = 'start',
  side = 'bottom',
  triggerClassName,
  onChange,
}) => {
  const [open, setOpen] = React.useState(false)

  const selectedType = PURSUIT_TYPES.find((type) => type === value)

  const handleChange = (value: PursuitType) => {
    onChange(value)
    setOpen(false)
  }

  return (
    <Combobox open={open} onOpenChange={setOpen}>
      <ComboboxTrigger asChild={true}>
        <ComboboxButton className={triggerClassName}>
          {selectedType ? (
            <>
              <span className="px-1">
                {getOpportunityLabelFromPursuitType(selectedType)}
              </span>
            </>
          ) : (
            <span className="pl-1 text-primary-500 dark:text-primary-400">
              + Select type
            </span>
          )}
        </ComboboxButton>
      </ComboboxTrigger>
      <ComboboxContent className="p-0 sm:max-w-48" side={side} align={align}>
        <ComboboxInput
          placeholder="Change type..."
          className="placeholder:text-[13px]"
        />
        <ComboboxList>
          <ComboboxEmpty className="text-[13px]">
            No results found.
          </ComboboxEmpty>
          <ComboboxGroup>
            {PURSUIT_TYPES.map((type) => {
              const isSelected = type === value
              return (
                <ComboboxSelectableItem
                  key={type}
                  value={type}
                  onSelect={() => handleChange(type)}
                  isSelected={isSelected}
                >
                  <div className="flex items-center gap-x-2">
                    <span>{getOpportunityLabelFromPursuitType(type)}</span>
                  </div>
                </ComboboxSelectableItem>
              )
            })}
          </ComboboxGroup>
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  )
}

TypeSelect.displayName = 'PursuitForm.TypeSelect'
